import { type FC, isValidElement, useContext } from 'react'
import { getUiOptions, type ArrayFieldTemplateItemType, type ArrayFieldTemplateProps, type RJSFSchema } from '@rjsf/utils'
import { type ParametersFormContext } from '../formContext'
import { isPlainObject, type JsonObject } from '../mergePatch'
import { ArrayRows } from './ArrayRows'
import { hasErrors, listConfigOf } from './arrayModel'
import { headingOf } from './displayLabel'
import { ExcludedOptionsContext } from './excludedOptions'
import { ExpandableRows } from './ExpandableRows'
import { GroupHeading } from './GroupHeading'
import { RowSummary } from './RowSummary'

const itemErrorSchemaOf = (item: ArrayFieldTemplateItemType<JsonObject, RJSFSchema, ParametersFormContext>): unknown =>
  isValidElement<{ errorSchema?: unknown }>(item.children) ? item.children.props.errorSchema : undefined

export const ArrayFieldTemplate: FC<ArrayFieldTemplateProps<JsonObject, RJSFSchema, ParametersFormContext>> = ({
  items,
  canAdd,
  onAddClick,
  disabled,
  readonly,
  schema,
  title,
  idSchema,
  registry,
  uiSchema,
  formData,
}) => {
  const parentExcluded = useContext(ExcludedOptionsContext)
  const config = listConfigOf(schema)
  const isRoot = idSchema.$id === registry.formContext.rootId
  const options = getUiOptions(uiSchema)
  const uniqueField = typeof options.uniqueField === 'string' ? options.uniqueField : undefined
  const listProps = {
    items,
    canAdd: Boolean(canAdd) && !readonly,
    locked: Boolean(disabled || readonly),
    noun: config.noun,
    emptyText: config.emptyText,
    onAddClick,
  }
  const heading = isRoot ? null : <GroupHeading title={headingOf(schema, title)} description={schema.description} />

  if (config.layout === 'rows') {
    return (
      <div className='d-flex flex-column'>
        {heading}
        <ArrayRows {...listProps} />
      </div>
    )
  }

  const values: unknown[] = Array.isArray(formData) ? formData : []
  const itemsUiSchema = isPlainObject(uiSchema?.items) ? uiSchema.items : undefined
  const pickedIn = (index: number): unknown => {
    const item = values[index]
    return uniqueField && isPlainObject(item) ? item[uniqueField] : undefined
  }
  const excludedFor = (fieldId: string): unknown[] => {
    const index = uniqueField ? values.findIndex((_value, candidate) => fieldId === `${idSchema.$id}_${candidate}_${uniqueField}`) : -1
    return index < 0
      ? parentExcluded(fieldId)
      : values.map((_value, other) => (other === index ? undefined : pickedIn(other))).filter((picked) => picked !== undefined && picked !== '')
  }

  return (
    <div className='d-flex flex-column'>
      {heading}
      <ExcludedOptionsContext.Provider value={excludedFor}>
        <ExpandableRows
          {...listProps}
          errorIndexes={items.filter((item) => hasErrors(itemErrorSchemaOf(item))).map((item) => item.index)}
          bodyIdPrefix={idSchema.$id}
          generation={registry.formContext.generation}
          renderSummary={(item) => (
            <RowSummary arraySchema={schema} itemsUiSchema={itemsUiSchema} value={values[item.index]} index={item.index} layer={registry.formContext.layer} />
          )}
        />
      </ExcludedOptionsContext.Provider>
    </div>
  )
}
